import { Text, Input, Button, Flex, Form, Grid, TextArea } from '@pega/cosmos-react-core';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { appState } from '../../../interfaces/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { validateApiKey } from '../../SocialMedia/Chat/Tabs/LanguageTab/ApiKeyValidation';
import { addNewLanguageServiceAccount } from './LanguageServiceApis';

type languageFormData = {
  accountName: string;
  apiKey: string;
};

const CreateLanguageService: React.FC = () => {
  const { register, handleSubmit, errors, reset, formState } = useForm<languageFormData>({ mode: 'all' });
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const navigate = useNavigate();

  const onSubmit = async ({ accountName, apiKey }: languageFormData) => {
    if (formState.touched) {
      dispatch(updateLoaderDetails(true));
      const status = await validateApiKey(apiKey);
      const payload = {
        accountName: accountName,
        apiKey: apiKey,
        isApiKeyValid: status,
      };
      const json = await addNewLanguageServiceAccount(sessionId, payload);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        reset();
        navigate(`/language-service/${json?.data?.connectionId}`);
        dispatch(updateBannerInfo(`${translate('language_service')} ${translate('successfully_created')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(`${translate('invalid_session')}`, 'urgent'));
      } else {
        let errorMessage;
        if (json?.data?.code) {
          errorMessage = translate(json.data.code);
        }
        dispatch(updateBannerInfo(errorMessage || translate('lang_service_creation_failure'), 'urgent'));
      }
    }
  };

  const actions = (
    <Flex>
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const headerElement = () => {
    return (
      <Text variant="h1" as="h1">
        {`${translate('new')} ${translate('language_service')}`}
      </Text>
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      <Grid
        container={{
          cols: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        <Form actions={actions} className="basic-form" onSubmit={handleSubmit(onSubmit)}>
          <Input
            name="accountName"
            label={translate('name')}
            required={true}
            autoComplete="off"
            info={errors.accountName?.message}
            status={errors.accountName ? 'error' : undefined}
            ref={register({ required: translate('name_empty') })}
          />

          <TextArea
            name="apiKey"
            label={translate('api_key')}
            autoComplete="off"
            type="text"
            required={true}
            info={errors.apiKey?.message}
            status={errors.apiKey ? 'error' : undefined}
            ref={register({ required: translate('api_key_error') })}
          />
        </Form>
      </Grid>
    </SectionRenderTemplate>
  );
};

export default CreateLanguageService;
